import React, { useCallback, useEffect, useRef, useState } from 'react';
import { loadMoonPay } from '@moonpay/moonpay-js';
import type { MoonPayWebSdk } from '@moonpay/moonpay-js';

export type MoonPayFlow = 'buy' | 'sell';

export interface MoonPayWidgetButtonProps {
  flow: MoonPayFlow;
  disabled?: boolean;
  className?: string;
  /** Override button label (default: Buy/Sell with MoonPay). */
  label?: string;
  onSuccess?: (payload: unknown) => void;
  onClose?: () => void;
  /** Required in production when passing walletAddress, email, walletAddresses, etc. */
  signature?: string;
  /** MoonPay widget query params (baseCurrencyCode, defaultCurrencyCode, baseCurrencyAmount, …). */
  extraParams?: Record<string, string>;
  /** Open widget once when params become available (e.g. after pay page load). */
  autoOpen?: boolean;
}

function moonPayEnvironment(): 'sandbox' | 'production' {
  return process.env.REACT_APP_MOONPAY_ENVIRONMENT === 'production' ? 'production' : 'sandbox';
}

function moonPayApiKey(): string {
  return (process.env.REACT_APP_MOONPAY_API_KEY ?? '').trim();
}

const MoonPayWidgetButton: React.FC<MoonPayWidgetButtonProps> = ({
  flow,
  disabled = false,
  className = 'btn btn-outline-primary',
  label,
  onSuccess,
  onClose,
  signature,
  extraParams,
  autoOpen = false,
}) => {
  const sdkRef = useRef<MoonPayWebSdk | null>(null);
  const mountedRef = useRef(true);
  const autoOpenedRef = useRef(false);
  const [launching, setLaunching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const defaultLabel = flow === 'buy' ? 'Buy crypto (MoonPay)' : 'Sell crypto (MoonPay)';

  const cleanup = useCallback(() => {
    sdkRef.current?.close();
    sdkRef.current = null;
  }, []);

  const openWidget = useCallback(async () => {
    if (disabled || launching || typeof window === 'undefined') return;
    if (!extraParams?.baseCurrencyCode) {
      setError('Payment currency is still loading. Try again in a moment.');
      return;
    }

    const apiKey = moonPayApiKey();
    if (!apiKey) {
      setError('MoonPay is not configured. Set REACT_APP_MOONPAY_API_KEY in your environment.');
      return;
    }

    setError(null);
    setLaunching(true);

    try {
      const moonPayInit = await loadMoonPay();
      if (!moonPayInit) {
        throw new Error('MoonPay SDK unavailable');
      }

      cleanup();

      const handlers = {
        onClose: async () => {
          if (mountedRef.current) onClose?.();
        },
        onTransactionCompleted: async (props: unknown) => {
          if (mountedRef.current) onSuccess?.(props);
        },
      };

      const env = moonPayEnvironment();
      const widgetParams = {
        apiKey,
        theme: 'dark' as const,
        ...(signature ? { signature } : {}),
        ...extraParams,
        ...(flow === 'buy' && !extraParams.defaultCurrencyCode
          ? { defaultCurrencyCode: 'usdc_polygon' }
          : {}),
      };

      let sdk: MoonPayWebSdk | null | undefined;

      if (flow === 'buy') {
        sdk = moonPayInit({
          flow: 'buy',
          environment: env,
          variant: 'overlay',
          params: widgetParams,
          handlers,
        });
      } else {
        sdk = moonPayInit({
          flow: 'sell',
          environment: env,
          variant: 'overlay',
          params: {
            ...widgetParams,
            defaultBaseCurrencyCode: extraParams.defaultCurrencyCode ?? 'eth',
          },
          handlers,
        });
      }

      if (!sdk) {
        throw new Error('MoonPay SDK returned no instance');
      }

      sdkRef.current = sdk;
      sdk.show();
    } catch (e) {
      console.error('MoonPay open failed', e, extraParams);
      if (mountedRef.current) {
        const message = e instanceof Error ? e.message : 'Could not open MoonPay';
        setError(message.includes('MoonPay') ? message : 'Could not open MoonPay. Refresh and try again.');
      }
    } finally {
      if (mountedRef.current) setLaunching(false);
    }
  }, [cleanup, disabled, extraParams, flow, launching, onClose, onSuccess, signature]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      cleanup();
    };
  }, [cleanup]);

  useEffect(() => {
    if (!autoOpen || disabled || autoOpenedRef.current) return;
    if (!extraParams?.baseCurrencyCode) return;

    autoOpenedRef.current = true;
    void openWidget();
  }, [autoOpen, disabled, extraParams, openWidget]);

  return (
    <div className="inline-block">
      <button
        type="button"
        className={className}
        onClick={() => void openWidget()}
        disabled={disabled || launching || !extraParams?.baseCurrencyCode}
      >
        {launching ? 'Opening MoonPay…' : (label ?? defaultLabel)}
      </button>
      {error && (
        <p className="mt-1 mb-0 text-[11px] text-red-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

export default MoonPayWidgetButton;
